import { Droppable, Draggable } from '@hello-pangea/dnd';
import { ChevronDown, GripVertical, Plus, UserPlus, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { formatDurationMinutes } from 'helpers/dateTimes';

import { buildZoneColorByReferent, FALLBACK_COLOR, initials, UNASSIGNED_COLOR } from '../constants/approColors';
import {
    approIdOf,
    buildTourStops,
    compareTourPosition,
    daysStatus,
    estimateTour,
    fillStatus,
    groupByKey,
    groupRowsBySite,
    motifFor,
    regroupementRankLabel,
    STATUS_TOKEN,
    sumMetrics,
    TOUR_ESTIMATE_HINT,
    type MetricStatus,
    type PlanningTableRow,
    type TourStop,
} from '../constants/dynamicDeliveryColumns';

// Regroupement key = dnd draggableId = `${approId}|${rank}`. One codec for the selection key, the draggable
// id, and the page handlers that parse it back. Appro ids never contain "|".
export const regroupementKey = (approId: string, rank: number): string => `${approId}|${rank}`;

export const parseRegroupementKey = (key: string): { approId: string; rank: number } => {
    const separator = key.lastIndexOf('|');
    return { approId: key.slice(0, separator), rank: Number(key.slice(separator + 1)) };
};

type PlanningTourListProps = {
    rows: PlanningTableRow[];
    selectedKeys: string[];
    onSelect: (ids: string[]) => void;
    focusedApproIds: string[];
    onFocusAppro: (approId: string) => void;
    // Deliverers without any column today, added by hand as empty drop targets for cross-appro drags.
    extraAppros: { _id: string; name: string }[];
    onAddAppro: () => void;
    // True while a regroupement card is being dragged (page-level DragDropContext). Collapsed appros then
    // expose a drop strip — a collapsed section otherwise has a zero-height droppable and can't receive.
    isDragActive: boolean;
    collapseAll: boolean;
    collapseToken: number;
    // « Ajouter à une tournée existante »: while active, clicking a regroupement card picks it as the insertion
    // target (appro + rank) instead of selecting its columns.
    insertMode: boolean;
    onPickRegroupement: (approId: string, rank: number) => void;
    // Tour edit mode: clicking a regroupement card (de)selects the whole regroupement (multi-select) to move it
    // in bulk. `selectionNumberByKey` keys are `${approId}|${rank}` (same format as draggableId); the value is the
    // 1-based click order. Here only membership (`.has`) is used — the card highlight.
    editMode: boolean;
    selectionNumberByKey: Map<string, number>;
    onToggleRegroupement: (approId: string, ranks: number[]) => void;
    // A site's potential columns (not yet in delivery) are shown directly under the stop, addable in one click
    // (catch a forgotten column on a site already being visited).
    onAddPotential: (columnPlanningIds: string[], toApproId: string) => void;
    // Render layout: 'stack' (vertical list, default) or 'columns' (edit-mode side-by-side board).
    // `approFilter` restricts which tours show (left = ¬focused, board = focused); `showAddAppro` hides the
    // "add appro" button in the board.
    layout?: 'stack' | 'columns';
    approFilter?: (approId: string) => boolean;
    showAddAppro?: boolean;
};

// Tour-list rendering of the shared threshold statuses: only critical/required are highlighted, ok/unknown
// stay unstyled.
type Status = 'danger' | 'warning' | undefined;

const statusOf = (status: MetricStatus): Status =>
    status === 'critical' ? 'danger' : status === 'required' ? 'warning' : undefined;

type Regroupement = {
    rank: number;
    rows: PlanningTableRow[];
    kg: number;
    zone: PlanningTableRow['deliveryZone'];
};
type ApproGroup = {
    approId: string;
    name: string;
    color: string;
    columns: number;
    kg: number;
    regroupements: Regroupement[];
};

const buildGroups = (rows: PlanningTableRow[], approColors?: Map<string, string>): ApproGroup[] =>
    [...groupByKey(rows, approIdOf).entries()]
        .map(([approId, approRows]) => {
            const regroupements: Regroupement[] = [...groupByKey(approRows, (row) => String(row.rank)).values()]
                .map((rankRows) => {
                    const rows = [...rankRows].sort(compareTourPosition);
                    return {
                        rank: rankRows[0].rank,
                        rows,
                        kg: sumMetrics(rankRows).kg,
                        zone: rankRows[0].deliveryZone ?? null,
                    };
                })
                // Delivery sequence (manual day override → zone order → rank), keyed by each group's first column.
                .sort((a, b) => compareTourPosition(a.rows[0], b.rows[0]));

            return {
                approId,
                name: approRows[0]?.assignedDeliverer?.profile.fullname ?? 'Non assigné',
                color: approColors?.get(approRows[0]?.assignedDeliverer?._id ?? '') ?? UNASSIGNED_COLOR,
                columns: approRows.length,
                kg: sumMetrics(approRows).kg,
                regroupements,
            };
        })
        .sort((a, b) => a.name.localeCompare(b.name, 'fr'));

// The displayed group of one appro among the "à livrer" rows — shared base of the page's drag handlers.
const deliverGroupOf = (rows: PlanningTableRow[], approId: string): ApproGroup | undefined =>
    buildGroups(rows.filter((row) => row.isDeliver)).find((group) => group.approId === approId);

// An appro's regroupements as ordered { rank, ids }, in the exact display order. Rank-aware base shared by
// the drag reorder and the « Réordonner dans l'ordre de sélection » action (which permutes selected ranks).
export const orderedRegroupementsOf = (rows: PlanningTableRow[], approId: string): { rank: number; ids: string[] }[] =>
    deliverGroupOf(rows, approId)?.regroupements.map((rg) => ({ rank: rg.rank, ids: rg.rows.map((row) => row._id) })) ??
    [];

// An appro's regroupements as ordered lists of planning ids, in the exact display order.
// Used by the page's drag handler to compute the new tour order after a within-appro reorder.
export const orderedRegroupementPlanningIds = (rows: PlanningTableRow[], approId: string): string[][] =>
    orderedRegroupementsOf(rows, approId).map((rg) => rg.ids);

// The planning ids of one regroupement (approId + rank) of the "à livrer" rows.
// Used by the page's drag handler to reassign exactly the rows shown on the dragged card.
export const planningIdsOfRegroupement = (rows: PlanningTableRow[], approId: string, rank: number): string[] =>
    deliverGroupOf(rows, approId)
        ?.regroupements.find((rg) => rg.rank === rank)
        ?.rows.map((row) => row._id) ?? [];

const groupRowsByMachine = (rows: PlanningTableRow[]): { key: string; rows: PlanningTableRow[] }[] =>
    [...groupByKey(rows, (row) => row.column?.machineCode || row.column?._id || row._id).entries()].map(
        ([key, machineRows]) => ({ key, rows: machineRows }),
    );

const ColumnRowItem = ({
    row,
    isSelected,
    isOutlined,
    onSelect,
}: {
    row: PlanningTableRow;
    isSelected: boolean;
    isOutlined: boolean;
    onSelect: (ids: string[]) => void;
}) => {
    const motif = motifFor(row);
    return (
        <ColumnRow
            data-row-id={row._id}
            $selected={isSelected}
            $outlined={isOutlined}
            onClick={() => onSelect([row._id])}
        >
            <ColName title={row.column?.name ?? undefined}>{row.column?.name ?? '—'}</ColName>
            {motif ? <MotifTag $status={motif.status}>{motif.label}</MotifTag> : null}
            <ColMetrics>
                <Metric
                    $status={statusOf(fillStatus(row.metrics?.transactionalFillRatePercent, row.thresholds))}
                    title="Stock transactionnel"
                >
                    {row.metrics?.transactionalFillRatePercent != null
                        ? `${row.metrics.transactionalFillRatePercent.toFixed(0)}%`
                        : '—'}
                </Metric>
                <Metric $status={statusOf(daysStatus(row.metrics?.daysSinceLastDelivery, row.thresholds))}>
                    {row.metrics?.daysSinceLastDelivery ?? '—'}j
                </Metric>
            </ColMetrics>
        </ColumnRow>
    );
};

// One potential column of a selected site: same visual as a delivered column (name · motif · % · j) plus an
// « Ajouter » button. Not selectable (it isn't in the tour yet) — the button activates + assigns it.
const PotentialColumnRow = ({ row, onAdd }: { row: PlanningTableRow; onAdd: () => void }) => {
    const motif = motifFor(row);
    return (
        <PotentialRow>
            <ColName title={row.column?.name ?? undefined}>{row.column?.name ?? '—'}</ColName>
            {motif ? <MotifTag $status={motif.status}>{motif.label}</MotifTag> : null}
            <ColMetrics>
                <Metric
                    $status={statusOf(fillStatus(row.metrics?.transactionalFillRatePercent, row.thresholds))}
                    title="Stock transactionnel"
                >
                    {row.metrics?.transactionalFillRatePercent != null
                        ? `${row.metrics.transactionalFillRatePercent.toFixed(0)}%`
                        : '—'}
                </Metric>
                <Metric $status={statusOf(daysStatus(row.metrics?.daysSinceLastDelivery, row.thresholds))}>
                    {row.metrics?.daysSinceLastDelivery ?? '—'}j
                </Metric>
            </ColMetrics>
            <AddPotentialButton
                type="button"
                onClick={(event) => {
                    event.stopPropagation();
                    onAdd();
                }}
                data-test="planning-add-potential"
            >
                <Plus size={13} />
                Ajouter
            </AddPotentialButton>
        </PotentialRow>
    );
};

const SiteStopColumns = ({
    siteStop,
    badgeNumber,
    color,
    showHead,
    selected,
    onSelect,
    potentialRows,
    approId,
    onAddPotential,
}: {
    siteStop: TourStop;
    badgeNumber: number | undefined;
    color: string;
    showHead: boolean;
    selected: Set<string>;
    onSelect: (ids: string[]) => void;
    potentialRows: PlanningTableRow[];
    approId: string;
    onAddPotential: (columnPlanningIds: string[], toApproId: string) => void;
}) => (
    <SiteStop>
        {showHead && (
            <SiteStopHead>
                <OrderBadge style={{ backgroundColor: color }} data-test="planning-stop-badge">
                    {badgeNumber}
                </OrderBadge>
                <ColSite title={siteStop.siteName}>{siteStop.siteName}</ColSite>
            </SiteStopHead>
        )}
        {groupRowsByMachine(siteStop.rows).map((machine) => {
            const machineSelected = machine.rows.every((row) => selected.has(row._id));
            return (
                <MachineGroup key={machine.key} $selected={machineSelected}>
                    {machine.rows.map((row) => (
                        <ColumnRowItem
                            key={row._id}
                            row={row}
                            isSelected={selected.has(row._id)}
                            isOutlined={selected.has(row._id) && !machineSelected}
                            onSelect={onSelect}
                        />
                    ))}
                </MachineGroup>
            );
        })}
        {/* Potential columns at THIS site, shown directly under the stop (no click needed) — catch a forgotten
            column on a site already being visited. Clicks stay inside the block (bubbling would toggle the card). */}
        {potentialRows.length > 0 && (
            <PotentialBlock onClick={(event) => event.stopPropagation()}>
                <PotentialHead>
                    <span>Colonnes potentielles à ce site ({potentialRows.length})</span>
                    {potentialRows.length > 1 && (
                        <AddPotentialButton
                            type="button"
                            onClick={() =>
                                onAddPotential(
                                    potentialRows.map((row) => row._id),
                                    approId,
                                )
                            }
                            data-test="planning-add-potential-all"
                        >
                            <Plus size={13} />
                            Tout ajouter
                        </AddPotentialButton>
                    )}
                </PotentialHead>
                {potentialRows.map((row) => (
                    <PotentialColumnRow key={row._id} row={row} onAdd={() => onAddPotential([row._id], approId)} />
                ))}
            </PotentialBlock>
        )}
    </SiteStop>
);

export const PlanningTourList = ({
    rows,
    selectedKeys,
    onSelect,
    focusedApproIds,
    onFocusAppro,
    extraAppros,
    onAddAppro,
    isDragActive,
    collapseAll,
    collapseToken,
    insertMode,
    onPickRegroupement,
    editMode,
    selectionNumberByKey,
    onToggleRegroupement,
    onAddPotential,
    layout = 'stack',
    approFilter,
    showAddAppro = true,
}: PlanningTourListProps) => {
    const deliverRows = useMemo(() => rows.filter((row) => row.isDeliver), [rows]);
    // Potential (not-yet-delivered) columns grouped by site. `rows` is rank-sorted upstream, so each group is
    // already in priority order. Used to reveal a selected stop's forgotten siblings under its card.
    const potentialBySite = useMemo(
        () =>
            groupByKey(
                rows.filter((row) => !row.isDeliver),
                (row) => row.site?._id ?? '',
            ),
        [rows],
    );
    const approColors = useMemo(() => buildZoneColorByReferent(deliverRows), [deliverRows]);
    const groups = useMemo(() => buildGroups(deliverRows, approColors), [deliverRows, approColors]);
    // Tours actually shown in this instance (cheap filter on already-built groups): in edit mode the left list
    // excludes focused tours, the board keeps only them.
    const visibleGroups = approFilter ? groups.filter((group) => approFilter(group.approId)) : groups;
    const selected = useMemo(() => new Set(selectedKeys), [selectedKeys]);
    const focused = useMemo(() => new Set(focusedApproIds), [focusedApproIds]);
    // Shared stop sequence (one stop = one site): the SAME structure numbers the map markers, so a column's
    // badge here always matches its marker number — columns of one site share their stop number.
    const stopsByAppro = useMemo(() => buildTourStops(deliverRows), [deliverRows]);
    const orderByRowId = useMemo(() => {
        const map = new Map<string, number>();
        for (const stops of stopsByAppro.values()) {
            stops.forEach((stop, index) => stop.rows.forEach((row) => map.set(row._id, index + 1)));
        }
        return map;
    }, [stopsByAppro]);
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
    // Collapsed regroupement cards (key = draggableId): the card header stays visible and draggable, its
    // column list is hidden — same gesture as the appro sections, one level down.
    const [collapsedCards, setCollapsedCards] = useState<Set<string>>(new Set());
    const listRef = useRef<HTMLDivElement>(null);
    // Previous focus set, to detect which tour was JUST focused (so we scroll to that one only).
    const prevFocusedRef = useRef<string[]>(focusedApproIds);

    const toggleCollapsed = (approId: string) =>
        setCollapsed((current) => {
            const next = new Set(current);
            if (next.has(approId)) next.delete(approId);
            else next.add(approId);
            return next;
        });

    const toggleCardCollapsed = (cardKey: string) =>
        setCollapsedCards((current) => {
            const next = new Set(current);
            if (next.has(cardKey)) next.delete(cardKey);
            else next.add(cardKey);
            return next;
        });

    useEffect(() => {
        if (collapseToken === 0) return;
        const allCardKeys = groups.flatMap((appro) =>
            appro.regroupements.map((rg) => regroupementKey(appro.approId, rg.rank)),
        );
        // "Expand/Collapse All" only affects the CARDS (columns): supply sections remain expanded so that
        // the cards—and their stop-order badge—stay visible ("order always visible").
        setCollapsed(new Set());
        setCollapsedCards(collapseAll ? new Set(allCardKeys) : new Set());
    }, [collapseToken]);

    // Keep the focused column in view when it is picked from the map (site markers select several columns:
    // scroll to the first one).
    useEffect(() => {
        if (selectedKeys.length === 0) return;
        const node = listRef.current?.querySelector(`[data-row-id="${selectedKeys[0]}"]`);
        node?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [selectedKeys]);

    // Clicking a tour on the map (its route or legend entry) focuses its appro; bring the matching section into
    // view here so its stops/columns are readable without scrolling the list by hand. Only a single, deliberate
    // focus scrolls — the legend's "focus all" adds many tours at once and must not yank the list around.
    useEffect(() => {
        const previous = prevFocusedRef.current;
        prevFocusedRef.current = focusedApproIds;
        const added = focusedApproIds.filter((id) => !previous.includes(id));
        if (added.length !== 1) return;
        const node = listRef.current?.querySelector(`[data-appro-id="${added[0]}"]`);
        node?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }, [focusedApproIds]);

    if (deliverRows.length === 0) {
        return (
            <Wrapper ref={listRef} $layout={layout}>
                <Empty>Aucune colonne à livrer pour cette date.</Empty>
            </Wrapper>
        );
    }

    return (
        <Wrapper ref={listRef} $layout={layout}>
            {layout === 'columns' && visibleGroups.length === 0 && (
                <Empty>Cliquez sur une tournée sur la carte (légende ou tracé) pour la comparer ici.</Empty>
            )}
            {visibleGroups.map((appro) => {
                const isCollapsed = collapsed.has(appro.approId);
                const isFocused = focused.has(appro.approId);
                const stops = stopsByAppro.get(appro.approId) ?? [];
                const estimate = estimateTour(stops);
                return (
                    <ApproSection
                        key={appro.approId}
                        $focused={isFocused}
                        $layout={layout}
                        data-appro-id={appro.approId}
                        data-test="planning-tour-appro"
                    >
                        <ApproHead onClick={() => onFocusAppro(appro.approId)} $focused={isFocused}>
                            <Avatar style={{ backgroundColor: appro.color }}>{initials(appro.name)}</Avatar>
                            <ApproIdent>
                                <ApproName>{appro.name}</ApproName>
                                <ApproStats title={TOUR_ESTIMATE_HINT} data-test="planning-tour-appro-stats">
                                    {stops.length} arrêt{stops.length > 1 ? 's' : ''} · {appro.columns} col. ·{' '}
                                    <b>{appro.kg.toFixed(1)}</b> kg
                                    {estimate.roadKm > 0 ? <> · ≈ {estimate.roadKm.toFixed(0)} km</> : null}
                                    {stops.length > 0 ? (
                                        <>
                                            {' · '}
                                            <Duration $over={estimate.isOverloaded}>
                                                ≈ {formatDurationMinutes(estimate.totalMinutes)}
                                                {estimate.isOverloaded ? ' ⚠' : ''}
                                            </Duration>
                                        </>
                                    ) : null}
                                </ApproStats>
                            </ApproIdent>
                            <CollapseButton
                                type="button"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    toggleCollapsed(appro.approId);
                                }}
                                aria-label={isCollapsed ? 'Déplier' : 'Replier'}
                                $collapsed={isCollapsed}
                            >
                                <ChevronDown size={16} />
                            </CollapseButton>
                            {/* Board (edit mode): explicit "remove tour", independent of the map (with many
                                columns the map can scroll off-screen, so defocusing via its legend isn't
                                reachable). stopPropagation: the header already toggles focus. */}
                            {layout === 'columns' && (
                                <RemoveTourButton
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onFocusAppro(appro.approId);
                                    }}
                                    aria-label={`Retirer ${appro.name} de la comparaison`}
                                    title="Retirer de la comparaison"
                                >
                                    <X size={16} />
                                </RemoveTourButton>
                            )}
                        </ApproHead>

                        {/* Droppable wraps ONLY the cards (header stays outside — required by hello-pangea). */}
                        <Droppable droppableId={appro.approId}>
                            {(dropProvided, dropSnapshot) => (
                                <DropZone
                                    ref={dropProvided.innerRef}
                                    {...dropProvided.droppableProps}
                                    $over={dropSnapshot.isDraggingOver}
                                    $collapsed={isCollapsed && !isDragActive}
                                >
                                    {/* Collapsed appro during a drag: keep a visible strip so it can still
                                        receive a cross-appro drop (otherwise the droppable is zero-height). */}
                                    {isCollapsed && isDragActive && (
                                        <EmptyDropHint>Déposez ici pour réaffecter à {appro.name}</EmptyDropHint>
                                    )}
                                    {!isCollapsed &&
                                        appro.regroupements.map((rg, index) => {
                                            const cardKey = regroupementKey(appro.approId, rg.rank);
                                            const isCardCollapsed = collapsedCards.has(cardKey);
                                            const cardSites = groupRowsBySite(rg.rows);
                                            // The whole card is the click target in two modes: insert (pick this
                                            // regroupement as the destination) and edit (toggle it in the bulk
                                            // selection). Column clicks then no-op and bubble up to the card handler.
                                            const isRegSelected = selectionNumberByKey.has(cardKey);
                                            const cardOnClick = insertMode
                                                ? () => onPickRegroupement(appro.approId, rg.rank)
                                                : editMode
                                                  ? () => onToggleRegroupement(appro.approId, [rg.rank])
                                                  : undefined;
                                            const cardOnSelect = insertMode || editMode ? () => undefined : onSelect;
                                            return (
                                                <Draggable key={cardKey} draggableId={cardKey} index={index}>
                                                    {(dragProvided, dragSnapshot) => (
                                                        <RegroupCard
                                                            ref={dragProvided.innerRef}
                                                            {...dragProvided.draggableProps}
                                                            {...dragProvided.dragHandleProps}
                                                            $dragging={dragSnapshot.isDragging}
                                                            $clickable={insertMode || editMode}
                                                            $highlight={insertMode || (editMode && isRegSelected)}
                                                            data-test="planning-regroup-card"
                                                            data-appro={appro.approId}
                                                            data-rank={rg.rank}
                                                            onClick={cardOnClick}
                                                        >
                                                            <RegroupTop>
                                                                <GripVertical size={14} />
                                                                <CardSites>
                                                                    {(isCardCollapsed
                                                                        ? cardSites
                                                                        : cardSites.slice(0, 1)
                                                                    ).map((siteStop) => (
                                                                        <CardSite key={siteStop.siteId}>
                                                                            <OrderBadge
                                                                                style={{
                                                                                    backgroundColor: appro.color,
                                                                                }}
                                                                                data-test="planning-stop-badge"
                                                                            >
                                                                                {orderByRowId.get(siteStop.rows[0]._id)}
                                                                            </OrderBadge>
                                                                            <CardSiteName title={siteStop.siteName}>
                                                                                {siteStop.siteName}
                                                                            </CardSiteName>
                                                                        </CardSite>
                                                                    ))}
                                                                </CardSites>
                                                                <RegroupStats>
                                                                    <span>{rg.rows.length} col.</span>
                                                                </RegroupStats>
                                                                <RankTag>{regroupementRankLabel(rg.rank)}</RankTag>
                                                                {/* hello-pangea ne démarre pas de drag depuis un
                                                                    élément interactif : le bouton reste cliquable. */}
                                                                <CollapseButton
                                                                    type="button"
                                                                    onClick={() => toggleCardCollapsed(cardKey)}
                                                                    aria-label={isCardCollapsed ? 'Déplier' : 'Réduire'}
                                                                    $collapsed={isCardCollapsed}
                                                                >
                                                                    <ChevronDown size={14} />
                                                                </CollapseButton>
                                                            </RegroupTop>
                                                            {!isCardCollapsed && (
                                                                <Columns>
                                                                    {cardSites.map((siteStop, siteIndex) => (
                                                                        <SiteStopColumns
                                                                            key={siteStop.siteId}
                                                                            siteStop={siteStop}
                                                                            badgeNumber={orderByRowId.get(
                                                                                siteStop.rows[0]._id,
                                                                            )}
                                                                            color={appro.color}
                                                                            showHead={siteIndex > 0}
                                                                            selected={selected}
                                                                            onSelect={cardOnSelect}
                                                                            potentialRows={
                                                                                potentialBySite.get(siteStop.siteId) ??
                                                                                []
                                                                            }
                                                                            approId={appro.approId}
                                                                            onAddPotential={onAddPotential}
                                                                        />
                                                                    ))}
                                                                </Columns>
                                                            )}
                                                        </RegroupCard>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                    {dropProvided.placeholder}
                                </DropZone>
                            )}
                        </Droppable>
                    </ApproSection>
                );
            })}

            {/* Hand-added deliverers with no column today: empty drop zones so a regroupement can be dragged
                onto them. They turn into real sections (with rows) after the drop + refetch. */}
            {showAddAppro &&
                extraAppros.map((appro) => (
                    <ApproSection key={appro._id} $focused={false} $layout={layout}>
                        <ApproHead $focused={false}>
                            <Avatar style={{ backgroundColor: FALLBACK_COLOR }}>{initials(appro.name)}</Avatar>
                            <ApproIdent>
                                <ApproName>{appro.name}</ApproName>
                                <ApproStats>Aucune colonne ce jour</ApproStats>
                            </ApproIdent>
                        </ApproHead>
                        <Droppable droppableId={appro._id}>
                            {(dropProvided, dropSnapshot) => (
                                <DropZone
                                    ref={dropProvided.innerRef}
                                    {...dropProvided.droppableProps}
                                    $over={dropSnapshot.isDraggingOver}
                                    $collapsed={false}
                                >
                                    <EmptyDropHint>Déposez un regroupement ici</EmptyDropHint>
                                    {dropProvided.placeholder}
                                </DropZone>
                            )}
                        </Droppable>
                    </ApproSection>
                ))}

            {showAddAppro && (
                <AddApproButton type="button" onClick={onAddAppro} data-test="planning-tour-add-appro">
                    <UserPlus size={15} />
                    Ajouter un appro
                </AddApproButton>
            )}
        </Wrapper>
    );
};

const Wrapper = styled.div<{ $layout: 'stack' | 'columns' }>`
    min-height: 0;
    display: flex;
    ${({ $layout }) =>
        $layout === 'columns'
            ? // Board (left pane in edit mode): sized to its content (the columns), capped so the map keeps a
              // full share on the right; horizontal scroll beyond that.
              `flex: 0 1 auto; max-width: 60%; min-width: 300px; flex-direction: row; overflow-x: auto; overflow-y: hidden; gap: 12px; padding-bottom: 4px;`
            : `flex: 0 0 400px; flex-direction: column; overflow-y: auto; gap: 8px; padding-right: 4px;`}
`;

const Empty = styled.div`
    padding: 24px;
    text-align: center;
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 13px;
`;

const ApproSection = styled.div<{ $focused: boolean; $layout: 'stack' | 'columns' }>`
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    transition: border-color 0.15s ease;
    /* In the board (columns) each tour is a standalone column: fixed width, its own vertical scroll, border
       always visible. In stack layout the border only shows on focus. */
    ${({ theme, $focused, $layout }) =>
        $layout === 'columns'
            ? `flex: 0 0 360px;
               min-width: 360px;
               max-height: 100%;
               overflow-y: auto;
               border: 1px solid ${$focused ? theme.ctaPrimaryColor : theme.lightBorderColor};
               background: ${theme.backgroundColor};`
            : `border: 1px solid ${$focused ? theme.ctaPrimaryColor : 'transparent'};
               background: ${$focused ? theme.backgroundColor : 'transparent'};`}
`;

const ApproHead = styled.div<{ $focused: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px;
    border-radius: 10px;
    cursor: pointer;

    &:hover {
        background: ${({ theme }) => theme.backgroundColor};
    }
`;

const Avatar = styled.span`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    font-weight: 700;
    font-size: 11px;
    color: #fff;
    flex-shrink: 0;
`;

const ApproIdent = styled.div`
    min-width: 0;
    flex: 1;
`;

const ApproName = styled.div`
    font-weight: 700;
    font-size: 14px;
    color: ${({ theme }) => theme.textColor};
`;

const ApproStats = styled.div`
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};

    b {
        color: ${({ theme }) => theme.textColor};
    }
`;

const Duration = styled.span<{ $over: boolean }>`
    color: ${({ theme, $over }) => ($over ? theme.errorColor : theme.textColor)};
    font-weight: ${({ $over }) => ($over ? 700 : 600)};
`;

const CollapseButton = styled.button<{ $collapsed: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    padding: 4px;
    cursor: pointer;
    color: ${({ theme }) => theme.infoTextColor};
    transition: transform 0.18s ease;
    transform: rotate(${({ $collapsed }) => ($collapsed ? '-90deg' : '0deg')});
    flex-shrink: 0;
`;

const RemoveTourButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    padding: 4px;
    cursor: pointer;
    color: ${({ theme }) => theme.infoTextColor};
    flex-shrink: 0;

    &:hover {
        color: ${({ theme }) => theme.errorColor};
    }
`;

const DropZone = styled.div<{ $over: boolean; $collapsed: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: ${({ $collapsed }) => ($collapsed ? '0' : '52px')};
    margin: 0 8px 8px 26px;
    padding: ${({ $over }) => ($over ? '6px' : '0')};
    border-radius: 10px;
    border: 1px dashed ${({ theme, $over }) => ($over ? theme.ctaPrimaryColor : 'transparent')};
    background: ${({ theme, $over }) => ($over ? theme.cardBackgroundColor : 'transparent')};
    transition:
        background 0.15s ease,
        border-color 0.15s ease,
        padding 0.15s ease;
`;

const RegroupCard = styled.div<{ $dragging: boolean; $clickable: boolean; $highlight: boolean }>`
    background: ${({ theme, $highlight }) =>
        $highlight
            ? `color-mix(in srgb, ${theme.ctaPrimaryColor} 10%, ${theme.cardBackgroundColor})`
            : theme.cardBackgroundColor};
    border: 1px solid ${({ theme, $highlight }) => ($highlight ? theme.ctaPrimaryColor : theme.lightBorderColor)};
    border-radius: 10px;
    padding: 9px 11px;
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'grab')};
    box-shadow: ${({ $dragging }) => ($dragging ? '0 12px 28px rgba(0, 0, 0, 0.3)' : 'none')};

    &:hover {
        border-color: ${({ theme, $clickable }) => ($clickable ? theme.ctaPrimaryColor : theme.infoTextColor)};
        ${({ theme, $clickable }) => ($clickable ? `background: ${theme.backgroundColor};` : '')}
    }

    &:active {
        cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'grabbing')};
    }
`;

const RegroupTop = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const CardSites = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    flex: 1;
`;

const CardSite = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

const CardSiteName = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const RankTag = styled.span`
    flex-shrink: 0;
    white-space: nowrap;
    font-size: 11px;
    font-weight: 700;
    color: ${({ theme }) => theme.textColor};
    background: ${({ theme }) => theme.backgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 6px;
    padding: 1px 6px;
`;

const RegroupStats = styled.span`
    margin-left: auto;
    display: flex;
    gap: 10px;
    font-size: 11px;
    color: ${({ theme }) => theme.infoTextColor};
    flex-shrink: 0;

    b {
        color: ${({ theme }) => theme.textColor};
    }
`;

const Columns = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 6px;
`;

const SiteStop = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const SiteStopHead = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 6px 2px 22px;
`;

const ColSite = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

/* One block per physical machine, indented under the stop's order badge (18px + 8px gap).
   No padding + clipping: the rows fill the box edge to edge, so the selection outline sits exactly
   on the machine border instead of floating 2px inside it. */
const MachineGroup = styled.div<{ $selected: boolean }>`
    display: flex;
    flex-direction: column;
    margin-left: 26px;
    background: ${({ theme }) => theme.backgroundColor};
    border-radius: 8px;
    overflow: hidden;
    box-shadow: ${({ theme, $selected }) => ($selected ? `inset 0 0 0 1.5px ${theme.ctaPrimaryColor}` : 'none')};
`;

const ColumnRow = styled.div<{ $selected: boolean; $outlined: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 10px;
    cursor: pointer;
    background: ${({ theme, $selected }) =>
        $selected ? `color-mix(in srgb, ${theme.ctaPrimaryColor} 16%, transparent)` : 'transparent'};

    &:hover {
        background: ${({ theme }) => `color-mix(in srgb, ${theme.textColor} 6%, transparent)`};
    }

    & + & {
        box-shadow: inset 0 1px 0 ${({ theme }) => `color-mix(in srgb, ${theme.textColor} 8%, transparent)`};
    }
`;

const OrderBadge = styled.span`
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 10px;
    font-weight: 700;
    color: #fff;
    flex-shrink: 0;
`;

const ColName = styled.div`
    flex: 1;
    min-width: 0;
    font-size: 11px;
    color: ${({ theme }) => theme.infoTextColor};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ColMetrics = styled.div`
    display: flex;
    gap: 10px;
    font-size: 11px;
    flex-shrink: 0;
`;

const EmptyDropHint = styled.div`
    border: 1px dashed ${({ theme }) => theme.lightBorderColor};
    border-radius: 8px;
    padding: 14px;
    text-align: center;
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const AddApproButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 0 8px 8px;
    padding: 10px;
    border: 1px dashed ${({ theme }) => theme.lightBorderColor};
    border-radius: 10px;
    background: none;
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 13px;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
        border-color: ${({ theme }) => theme.ctaPrimaryColor};
        color: ${({ theme }) => theme.ctaPrimaryColor};
    }
`;

const Metric = styled.span<{ $status?: Status }>`
    color: ${({ theme, $status }) =>
        $status === 'danger' ? theme.errorColor : $status === 'warning' ? theme.warningColor : theme.infoTextColor};
    font-weight: ${({ $status }) => ($status ? 600 : 400)};
`;

const MotifTag = styled.span<{ $status: MetricStatus }>`
    flex-shrink: 0;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
    color: ${({ theme, $status }) => theme[STATUS_TOKEN[$status]]};
`;

// "Potential columns at this site" block, shown under a stop: visually detached (dashed + light background)
// so it isn't confused with the delivered columns above.
const PotentialBlock = styled.div`
    margin: 6px 0 2px 26px;
    padding: 8px;
    border: 1px dashed ${({ theme }) => theme.lightBorderColor};
    border-radius: 8px;
    background: ${({ theme }) => `color-mix(in srgb, ${theme.infoTextColor} 6%, transparent)`};
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const PotentialHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const PotentialRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 6px;
    border-radius: 6px;

    &:hover {
        background: ${({ theme }) => `color-mix(in srgb, ${theme.textColor} 6%, transparent)`};
    }
`;

const AddPotentialButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    flex-shrink: 0;
    border: 1px solid ${({ theme }) => theme.ctaPrimaryColor};
    background: none;
    color: ${({ theme }) => theme.ctaPrimaryColor};
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
    cursor: pointer;

    &:hover {
        background: ${({ theme }) => `color-mix(in srgb, ${theme.ctaPrimaryColor} 12%, transparent)`};
    }
`;
